import { ChevronDown, Radio as RadioIcon, Volume2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PttButton, pttErrorText, radioLinkText, SpeakerBanner, useRadio } from '@despachador/ui';
import type { RadioClient, RadioSnapshot } from '@despachador/api-client/ptt';
import { useDriver } from '../store';

/** Who holds the channel, in words: "Canal libre" or "Carlos Pérez está hablando". */
function channelState(s: RadioSnapshot) {
  const live = Object.values(s.channels).find((c) => c.speaker && c.speaker.userId !== s.userId) ?? null;
  const active = s.activeChannelId ? s.channels[s.activeChannelId] : null;
  const self = active?.speaker?.userId === s.userId;
  return { live, active, self };
}

/**
 * Driver radio. The driver is driving: one channel name, who is talking, one big square button, the
 * connection state in words. Nothing else competes for attention.
 */
export function RadioPage() {
  const { radio } = useDriver();
  const s = useRadio(radio);
  if (!radio || !s)
    return (
      <div className="page-pad">
        <div className="card stack" role="status">
          <b>Radio no disponible</b>
          <div className="muted">Tu empresa no tiene la radio habilitada o aún se está cargando.</div>
        </div>
      </div>
    );
  return <RadioScreen radio={radio} s={s} />;
}

function RadioScreen({ radio, s }: { radio: RadioClient; s: RadioSnapshot }) {
  const [picking, setPicking] = useState(false);
  const { active, self } = channelState(s);
  const link = radioLinkText(s);
  const listenable = s.available.filter((c) => c.canListen);
  return (
    <section className="radio-driver page-pad" aria-labelledby="radio-title">
      <h1 id="radio-title" className="page-title">
        Radio
      </h1>

      <div className="radio-channel-field">
        <span className="field-label" id="radio-channel-label">
          Canal
        </span>
        <button
          type="button"
          className="radio-channel"
          onClick={() => setPicking((p) => !p)}
          aria-expanded={picking}
          aria-controls="radio-channels"
          aria-labelledby="radio-channel-label radio-channel-name"
          disabled={listenable.length < 2}
        >
          <b id="radio-channel-name">{active?.name ?? 'Sin canal'}</b>
          {listenable.length > 1 && <ChevronDown size={20} aria-hidden />}
        </button>
      </div>
      {picking && (
        <ul id="radio-channels" className="radio-channel-list" aria-label="Elegir canal">
          {listenable.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className={`btn lg block${c.id === s.activeChannelId ? ' primary' : ''}`}
                aria-current={c.id === s.activeChannelId}
                onClick={() => {
                  radio.selectChannel(c.id);
                  setPicking(false);
                }}
              >
                {c.name}
                {c.kind === 'DIRECT' ? ' (privado)' : ''}
                {!c.canTransmit ? ' · solo escucha' : ''}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* who holds the channel: a status line here (the shared banner, restyled) */}
      <SpeakerBanner speaker={active?.speaker ?? null} self={self} />

      {s.audioBlocked && (
        <button type="button" className="btn lg block warn-btn" onClick={() => void radio.enableAudio()}>
          <Volume2 size={20} aria-hidden /> Toca para activar el audio de la radio
        </button>
      )}
      {s.notice && s.link.phase === 'READY' && (
        <div className="error-box" role="alert">
          {pttErrorText(s.notice.code, s.notice.detail)}
        </div>
      )}

      <PttButton client={radio} snapshot={s} size="driver" />

      <p className={`radio-link ${link.tone}`} role="status">
        Estado: {link.text}
      </p>
    </section>
  );
}

/**
 * Radio on every screen without a trip (DRV-010): a fixed strip above the navigation, never floating
 * over the content. The channel and who is talking on the left (opens the radio), the talk button on
 * the right. During a trip the talk button moves into the next-step bar instead (RadioTalk).
 */
export function RadioStrip() {
  const { radio } = useDriver();
  const s = useRadio(radio);
  if (!radio || !s || s.link.phase === 'DISCONNECTED' || s.link.phase === 'ERROR') return null;
  const { live, active, self } = channelState(s);
  const state = self ? 'Estás al aire' : live ? `${live.speaker!.name} está hablando` : 'Canal libre';
  // without a microphone or permission to talk the strip only listens: one thin line (UX-028)
  const canTalk = !!active?.canTransmit && s.mic !== 'unavailable';
  return (
    <div className={canTalk ? 'radio-strip' : 'radio-strip compact'} aria-label="Radio">
      <Link to="/radio" className="radio-strip-info" aria-label={`Radio, canal ${active?.name ?? 'sin canal'}: ${state}. Abrir radio`}>
        <RadioIcon size={20} aria-hidden className="ico" />
        <span className="radio-strip-text">
          <span className="radio-strip-channel">{active?.name ?? 'Sin canal'}</span>
          <span className={`radio-strip-state${live || self ? ' live' : ''}`}>
            <span className="dot" aria-hidden />
            {state}
          </span>
        </span>
      </Link>
      {canTalk && <PttButton client={radio} snapshot={s} size="dock" />}
    </div>
  );
}

/** The talk button of the trip's next-step bar: push-to-talk without leaving the trip. */
export function RadioTalk() {
  const { radio } = useDriver();
  const s = useRadio(radio);
  if (!radio || !s || s.link.phase === 'DISCONNECTED' || s.link.phase === 'ERROR') return null;
  const active = s.activeChannelId ? s.channels[s.activeChannelId] : null;
  if (!active?.canTransmit) return null;
  return <PttButton client={radio} snapshot={s} size="dock" />;
}
